import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivityTag, ActivityTagName, Attachment, ComplianceQuestionUnique, DeleteAdditionalAttachmentRequest, ItemDeletedResponse, SaveAdditionFileRequest, SaveAdditionFileResponse, WorkComplianceUtility } from '@jms/model';
import { ToastService } from '@workbuddy/ui/services';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Item attachments, ported from the legacy items tab so the board writes to the same store.
 *
 * Attachments hang off the item's compliance record rather than the item, which is why every call is
 * keyed by `WorkComplianceUtility.getKey(...)` instead of the item id, and why the endpoint lives
 * under work compliance. Saving one also flips the item's `answered` flag server-side, and that flag
 * blocks deletion of the item — so an attachment is not a passive decoration.
 */
@Injectable()
export class ItemMediaService {

    private readonly http = inject(HttpClient);
    private readonly toastService = inject(ToastService);

    private readonly additionalFileApi = '/api/work/compliance/additional/file';

    /** The media document id is derived, not stored: stage + the compliance question + the item. */
    public getMediaId(stageId: string, itemId: string): string {

        return WorkComplianceUtility.getKey(stageId, ComplianceQuestionUnique, itemId);
    }

    public getMedia(mediaId: string): Observable<Attachment[]> {

        return this.http
            .get<Attachment[]>(`${this.additionalFileApi}/${mediaId}`)
            .pipe(
                catchError(() => of(null))
            );
    }

    public save(request: SaveAdditionFileRequest): Observable<SaveAdditionFileResponse> {

        request._id = request?._id ?? this.getMediaId(request?.workOrderId, request?.codeId);

        // Without this tag the file never appears in the job's Media tab — it is the only thing
        // linking the attachment back to the stage.
        request.tags = request.tags ?? [];
        request.tags.push({ name: ActivityTagName.WorkOrderId, value: request?.workOrderId } as ActivityTag);

        return this.http
            .post<SaveAdditionFileResponse>(this.additionalFileApi, request)
            .pipe(
                catchError(httpError => {

                    this.toastService.error('Save attachment', httpError?.error?.message);
                    return of(null);
                })
            );
    }

    public delete(request: DeleteAdditionalAttachmentRequest): Observable<ItemDeletedResponse> {

        return this.http
            .post<ItemDeletedResponse>(`${this.additionalFileApi}/delete`, request)
            .pipe(
                catchError(httpError => {

                    this.toastService.error('Delete attachment', httpError?.error?.message);
                    return of(null);
                })
            );
    }
}
